import type { ApiResponse } from '@/lib/api/types';
import { API_ERROR_CODES } from '@/lib/api/errors';

const SESSION_KEY = 'gfs_session';

/**
 * @interface SessionData
 * @description Canonical client-side auth session payload.
 */
export interface SessionData {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  user: { id: string; email: string };
  profile: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    altruConstituentId: string | null;
  };
}

/**
 * @interface LoginResponseData
 * @description Raw login response data shape returned by `/api/auth/login`.
 */
interface LoginResponseData {
  session: { accessToken: string; refreshToken: string; expiresAt: number };
  user: { id: string; email: string };
  profile: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    altruConstituentId: string | null;
  };
}

/**
 * @interface RefreshResponseData
 * @description Raw refresh response data shape returned by `/api/auth/refresh`.
 */
interface RefreshResponseData {
  session: { accessToken: string; refreshToken: string; expiresAt: number };
}

/**
 * @method getStorage
 * @description Returns the browser storage mechanism to use based on remember-me preference.
 * @param rememberMe - Whether session should persist across browser restarts.
 * @returns The selected browser Storage implementation.
 */
function getStorage(rememberMe: boolean): Storage {
  if (typeof window === 'undefined') throw new Error('Storage not available on server');
  return rememberMe ? localStorage : sessionStorage;
}

/**
 * @method getStoredSession
 * @description Reads the stored session from sessionStorage first, then localStorage.
 * @returns Parsed session data when present and valid, otherwise null.
 */
export function getStoredSession(): SessionData | null {
  if (typeof window === 'undefined') return null;
  const fromSession = sessionStorage.getItem(SESSION_KEY);
  const fromLocal = localStorage.getItem(SESSION_KEY);
  const raw = fromSession ?? fromLocal;

  if (!raw) return null;

  try {
    return JSON.parse(raw) as SessionData;
  } catch {
    clearSession();
    return null;
  }
}

/**
 * @method isExpired
 * @description Determines whether a UNIX expiry timestamp is already expired.
 * @param expiresAt - Token expiry timestamp in UNIX seconds.
 * @returns True when expiry is in the past; otherwise false.
 */
function isExpired(expiresAt: number): boolean {
  const expiresAtMs = expiresAt * 1000;
  return Date.now() >= expiresAtMs;
}

/**
 * @method loginWithCredentials
 * @description Authenticates a user against the login API and returns normalized session data.
 * @param email - User email address.
 * @param password - User password.
 * @returns A promise resolving to session, user, and profile details.
 * @throws {Error} When authentication fails or API returns an error.
 */
export async function loginWithCredentials(
  email: string,
  password: string
): Promise<SessionData> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const json: ApiResponse<LoginResponseData> = await res.json();

  if (!json.success || !json.data) {
    if (json.error?.code === API_ERROR_CODES.INVALID_CREDENTIALS) {
      throw new Error('Invalid email or password');
    }
    throw new Error(json.error?.message ?? 'Login failed');
  }

  const { session, user, profile } = json.data;
  return {
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    expiresAt: session.expiresAt,
    user,
    profile,
  };
}

/**
 * @method saveSession
 * @description Persists session data to localStorage or sessionStorage depending on remember-me preference.
 * @param session - Auth session payload to persist.
 * @param rememberMe - Whether to persist beyond the current browser session.
 * @returns Void.
 */
export function saveSession(session: SessionData, rememberMe: boolean): void {
  clearSession();
  getStorage(rememberMe).setItem(SESSION_KEY, JSON.stringify(session));
}

/**
 * @method clearSession
 * @description Clears all stored session data from both local and session storage.
 * @returns Void.
 */
export function clearSession(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SESSION_KEY);
}

/**
 * @method getSessionExpiresAt
 * @description Returns the stored session's expiry timestamp in UNIX seconds, or null when no session exists.
 * @returns Expiry timestamp in seconds, or null.
 */
export function getSessionExpiresAt(): number | null {
  const session = getStoredSession();
  return session ? session.expiresAt : null;
}

/**
 * @method getAccessToken
 * @description Returns the current access token only when a non-expired session exists.
 * @returns Access token string or null when unavailable/expired.
 */
export function getAccessToken(): string | null {
  const session = getStoredSession();
  if (!session) return null;

  if (isExpired(session.expiresAt)) {
    clearSession();
    return null;
  }

  return session.accessToken;
}

/**
 * @method renewSession
 * @description Renews an expired session using refresh token and rewrites storage with updated token values.
 * Can also proactively refresh a session when expiry is within the provided threshold.
 * @param proactiveRefreshThresholdSeconds - Optional threshold (in seconds) that triggers a refresh before expiry.
 * @returns True when a valid (existing or renewed) session is available; otherwise false.
 */
export async function renewSession(proactiveRefreshThresholdSeconds = 0): Promise<boolean> {
  const session = getStoredSession();
  if (!session) return false;

  const thresholdSeconds = Math.max(0, proactiveRefreshThresholdSeconds);
  const secondsUntilExpiry = session.expiresAt - Date.now() / 1000;
  const shouldProactivelyRefresh = secondsUntilExpiry <= thresholdSeconds;

  if (!isExpired(session.expiresAt) && !shouldProactivelyRefresh) {
    return true;
  }

  const fromLocal = !!localStorage.getItem(SESSION_KEY);

  const response = await fetch('/api/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: session.refreshToken }),
  });

  if (!response.ok) {
    clearSession();
    return false;
  }

  const json: ApiResponse<RefreshResponseData> = await response.json();
  if (!json.success || !json.data) {
    clearSession();
    return false;
  }

  saveSession(
    {
      ...session,
      accessToken: json.data.session.accessToken,
      refreshToken: json.data.session.refreshToken,
      expiresAt: json.data.session.expiresAt,
    },
    fromLocal
  );

  return true;
}

/**
 * @method logout
 * @description Logs out the authenticated user server-side and removes all client session state.
 * @returns A promise resolved when logout flow is complete.
 */
export async function logout(): Promise<void> {
  const token = getAccessToken();

  if (token) {
    await fetch('/api/auth/logout', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  clearSession();
}
